import { DOCUMENT, inject, Injectable } from '@angular/core';
import { distinctUntilChanged, Observable } from 'rxjs';

/**
 * `aria-modal` is the only marker every surface that owns the screen shares:
 * a lightbox overlay is created without a backdrop, an inline full-screen
 * panel is no CDK pane at all, and the CDK pane itself is also what tooltips,
 * menus and coach-marks are built from.
 */
const MODAL_SELECTOR = '[aria-modal="true"]';

@Injectable({ providedIn: 'root' })
export class ModalSurfaceService {
    private readonly document = inject(DOCUMENT);

    isAnyModalOpen(): boolean {
        return this.document.querySelector(MODAL_SELECTOR) != null;
    }

    /**
     * The last modal in document order that is not inert: CDK panes are
     * appended to the overlay container as they open, after the application
     * tree, and a modal playing its leave animation is made inert while it is
     * still in the document.
     */
    topModal(): Element | null {
        return (
            Array.from(this.document.querySelectorAll(MODAL_SELECTOR))
                .reverse()
                .find((modal) => modal.closest('[inert]') == null) ?? null
        );
    }

    /**
     * Emits the current answer on subscribe, then on every change. The
     * observer is created per subscription and torn down with it.
     */
    isAnyModalOpen$(): Observable<boolean> {
        return this.observeModals(() => this.isAnyModalOpen());
    }

    /** Same contract as {@link isAnyModalOpen$}. */
    topModal$(): Observable<Element | null> {
        return this.observeModals(() => this.topModal());
    }

    private observeModals<T>(read: () => T): Observable<T> {
        return new Observable<T>((subscriber) => {
            const observer = new MutationObserver((records) => {
                if (records.some(touchesModal)) {
                    subscriber.next(read());
                }
            });
            observer.observe(this.document.body, {
                attributeFilter: ['aria-modal', 'inert'],
                attributes: true,
                childList: true,
                subtree: true,
            });
            subscriber.next(read());

            return () => observer.disconnect();
        }).pipe(distinctUntilChanged());
    }
}

/**
 * `aria-modal` set to another value no longer matches the selector, so its
 * change always counts; `inert` only on a modal or an ancestor of one, since
 * {@link ModalSurfaceService.topModal} looks for it upwards from the modal.
 */
function touchesModal(record: MutationRecord): boolean {
    if (record.type === 'attributes') {
        return (
            record.attributeName === 'aria-modal' || holdsModal(record.target)
        );
    }

    return [
        ...Array.from(record.addedNodes),
        ...Array.from(record.removedNodes),
    ].some(holdsModal);
}

function holdsModal(node: Node): boolean {
    return (
        node instanceof Element &&
        (node.matches(MODAL_SELECTOR) ||
            node.querySelector(MODAL_SELECTOR) != null)
    );
}
